// Library Methods
import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  rectIntersection,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

// MUI Components
import { Grid, Typography, Box } from "@mui/material";

// Components
import ExamSliderCard from "../exam-slider-cards/ExamSliderCard";
import ExamOrderCard from "../exam-slider-cards/ExamOrderCard";

// Utilities
import useClinic from "../../../../hooks/useClinic";
import { useExamListContext } from "../../../../contexts/ExamListContext";
import { examCardEnums } from "../../../../enums";

const ExamCardList = () => {
  // internationalization
  const { t } = useTranslation();

  // clinic context
  const { clinicLicensing } = useClinic();
  const canUseExamPackaging = clinicLicensing?.canUseExamPackaging;

  const { examCards, actions } = useExamListContext();
  const { setExamCards } = actions;

  const [activeId, setActiveId] = useState(null);

  const selectedExamCards = useMemo(
    () =>
      examCards.selected.map((selectedExam) => {
        const enumValues = Object.values(examCardEnums);
        const label = enumValues.find(
          (enumValue) => enumValue.algorithmPath === selectedExam?.examFieldType
        )?.label;

        return { ...selectedExam, label };
      }),
    [examCards.selected]
  );

  const deselectedExamCards = useMemo(
    () => examCards.deselected,
    [examCards.deselected]
  );

  // SortableContext needs exam ids in a string array
  const examOrderIds = useMemo(
    () => selectedExamCards.map((exam) => exam?.examFieldType),
    [selectedExamCards]
  );

  const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  // Update exam order when dragging cards
  function handleDragEnd(event) {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }

    setExamCards((prev) => {
      const oldIndex = prev.selected.findIndex(
        (exam) => exam.examFieldType === active.id
      );
      const newIndex = prev.selected.findIndex(
        (exam) => exam.examFieldType === over.id
      );
      const newExamOrder = arrayMove(prev.selected, oldIndex, newIndex);

      const newSelectedOrder = newExamOrder
        .filter((exam) =>
          prev.selected.find((p) => exam.examFieldType === p.examFieldType)
        )
        .map((exam) =>
          prev.selected.find((p) => p.examFieldType === exam.examFieldType)
        );

      return {
        ...prev,
        selected: [...newSelectedOrder],
      };
    });

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const fixCursorSnapOffset = (args) => {
    // Return if keyboard activated
    if (!args.pointerCoordinates) {
      return rectIntersection(args);
    }
    const { x, y } = args.pointerCoordinates;
    const { width, height } = args.collisionRect;
    const updated = {
      ...args,
      // The collision rectangle is broken when using snapCenterToCursor
      // Reset the collision rectangle based on pointer location and overlay size
      collisionRect: {
        width,
        height,
        bottom: y + height / 2,
        left: x - width / 2,
        right: x + width / 2,
        top: y - height / 2,
      },
    };
    return rectIntersection(updated);
  };

  return (
    <>
      {canUseExamPackaging && (
        <Grid
          item
          display="flex"
          flexDirection="column"
          padding="20px 20px 0px 20px"
        >
          <Typography fontWeight={600}>{t("word_exam_order")}</Typography>
          {selectedExamCards?.length > 0 ? (
            <DndContext
              sensors={sensors}
              collisionDetection={fixCursorSnapOffset}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext
                items={examOrderIds}
                strategy={rectSortingStrategy}
              >
                <Box display="flex" flexDirection="column">
                  <Typography color="#000" fontSize="16px" textAlign="center">
                    {t("add_edit_multi_exam_packages")}
                  </Typography>
                  <Grid
                    item
                    display="flex"
                    justifyContent="center"
                    flexWrap="wrap"
                    gap="20px"
                    padding="20px"
                  >
                    {selectedExamCards.map((item) => (
                      <ExamOrderCard
                        key={item?.examFieldType}
                        id={item?.examFieldType}
                        label={t(item?.label)}
                        isDragMode={activeId}
                        isSelected
                      />
                    ))}
                  </Grid>
                </Box>
              </SortableContext>
            </DndContext>
          ) : (
            <Grid item height="82px" alignContent="center">
              <Typography color="#000" fontSize="16px" textAlign="center">
                {t("exam_order_error")}
              </Typography>
            </Grid>
          )}
        </Grid>
      )}

      {canUseExamPackaging && (
        <Grid item display="flex" padding="20px 20px 0px 20px">
          <Typography color="#000" fontWeight={600}>
            {t("word_exam_options")}
          </Typography>
        </Grid>
      )}

      <Grid
        item
        display="flex"
        justifyContent="center"
        flexWrap="wrap"
        gap="20px"
        padding="20px"
      >
        {selectedExamCards.map((item) => (
          <ExamSliderCard
            id={item?.examFieldType}
            key={item?.examFieldType}
            label={t(item?.label)}
            examAlgorithm={item?.examFieldType}
          />
        ))}
        {deselectedExamCards.map((item) => {
          return (
            <ExamSliderCard
              id={item?.algorithmPath}
              key={item?.algorithmPath}
              label={t(item?.label)}
              examAlgorithm={item?.algorithmPath}
            />
          );
        })}
      </Grid>
    </>
  );
};

export default ExamCardList;
